/**
 * Espionage and NPC pirate camps (GDD §3 "kleine NPC-Piraten",
 * §5 step 4 spy algorithm).
 *
 * This is what makes the Aero-Sonde and an offensive fleet worth owning before
 * PvP exists. Three connected pieces:
 *
 *  - **Camps.** Persistent NPC targets in the home system, rerolled on a timer.
 *    Each holds a garrison and a stash.
 *  - **Scouting.** Send probes; success is decided by the GDD's stated rule —
 *    compare tech levels. A successful probe reveals garrison and loot, a
 *    failed one is destroyed ("bei Misserfolg die Sonde zerstören").
 *  - **Raiding them back.** Send a fleet; the battle runs through the same
 *    simulator, the survivors come home with cargo-capped loot.
 */

import type { GameState, PirateCamp, UnitKey } from './types.js';
import { SHIPS, UNITS } from './data/units.js';
import { nextInt, nextRandom } from './rng.js';

/** Camps present at once. Small enough to read at a glance. */
export const CAMP_COUNT = 3;
/** The board rerolls this often. */
export const CAMP_REFRESH_MS = 6 * 3_600_000;
/** One-way travel time to a camp in the home system. */
export const CAMP_TRAVEL_MS = 20 * 60_000;

/** Garrison roster, weakest first — camp strength picks a slice of it. */
const GARRISON_ROSTER: UnitKey[] = ['corvette', 'gauss', 'cruiser', 'plasma', 'battleship'];

function unitPower(key: UnitKey): number {
  const def = UNITS[key];
  return def.damage * 2 + def.hull + def.shield;
}

/**
 * Roll a fresh camp board. Mutates rngSeed/nextId/camps.
 *
 * Camp strength is anchored to account level, not to the player's fleet: a
 * camp the player cannot yet crack is legitimate content to come back to,
 * whereas scaling to current fleet would make progress feel weightless.
 */
export function refreshCamps(state: GameState, now: number): void {
  const camps: PirateCamp[] = [];

  for (let i = 0; i < CAMP_COUNT; i++) {
    const tierRoll = nextInt(state.rngSeed, 0, 2);
    state.rngSeed = tierRoll.seed;
    // Tier 0 = soft target, 2 = a real fight for the current level.
    const tier = tierRoll.value;
    const budget = (400 + state.level * 260) * (0.6 + tier * 0.7);

    const garrison: Partial<Record<UnitKey, number>> = {};
    let remaining = budget;
    const roster = GARRISON_ROSTER.slice(0, 2 + tier);
    for (const key of [...roster].reverse()) {
      const power = unitPower(key);
      if (power > remaining) continue;
      const shareRoll = nextRandom(state.rngSeed);
      state.rngSeed = shareRoll.seed;
      const share = key === roster[0] ? 1 : 0.35 + shareRoll.value * 0.35;
      const count = Math.floor((remaining * share) / power);
      if (count > 0) {
        garrison[key] = count;
        remaining -= count * power;
      }
    }
    if (Object.keys(garrison).length === 0) garrison.corvette = 1;

    const lootRoll = nextInt(state.rngSeed, 6_000, 24_000);
    state.rngSeed = lootRoll.seed;
    const scale = 1 + state.level * 0.4;

    camps.push({
      id: state.nextId++,
      name: CAMP_NAMES[(state.nextId + i) % CAMP_NAMES.length]!,
      tier,
      /** Tech level the probe must beat — higher tier hides better. */
      sensorTech: tier,
      garrison,
      loot: {
        titan: Math.round(lootRoll.value * scale),
        silicate: Math.round(lootRoll.value * 0.6 * scale),
        isotope: Math.round(lootRoll.value * 0.15 * scale),
      },
      scouted: false,
      expiresAt: now + CAMP_REFRESH_MS,
    });
  }

  state.camps = camps;
  state.campsRefreshAt = now + CAMP_REFRESH_MS;
}

const CAMP_NAMES = [
  'Schrottnest Kor-7',
  'Bergungslager Vex',
  'Piraten-Kaverne Drossel',
  'Wrackring Halcyon',
  'Aussenposten Nul-3',
  'Versteck Sichel',
];

/**
 * Probe success (GDD §5 step 4: "die Tech-Stufen vergleichen").
 *
 * Each probe beyond the first adds a flat bonus, so sending more is the lever
 * the player controls. Astrometrische Triangulation sharpens the sensors.
 */
export function scoutSuccessChance(state: GameState, target: { sensorTech: number }, probes: number): number {
  const techEdge = state.research.astrometrics - target.sensorTech;
  const base = 0.45 + techEdge * 0.12 + (probes - 1) * 0.15;
  return Math.min(0.95, Math.max(0.05, base));
}

/** Independent defender-side detection chance for player espionage. */
export function counterEspionageChance(defenderTech: number, attackerProbes: number): number {
  const base = 0.12 + Math.max(0, defenderTech) * 0.11 - Math.max(0, attackerProbes - 1) * 0.035;
  return Math.min(0.9, Math.max(0.05, base));
}

/** Resolves detection from a supplied deterministic random roll. */
export function counterEspionageDetected(defenderTech: number, attackerProbes: number, roll: number): boolean {
  return roll < counterEspionageChance(defenderTech, attackerProbes);
}

/** Total cargo capacity of a fleet, including the Bergbau research bonus. */
export function fleetCargo(state: GameState, fleet: Partial<Record<UnitKey, number>>): number {
  let cargo = 0;
  for (const [key, count] of Object.entries(fleet) as [UnitKey, number][]) {
    const def = UNITS[key];
    if (def.kind === 'ship') cargo += (def.cargo ?? 0) * count;
  }
  return cargo * (1 + 0.15 * state.research.asteroidMining);
}

/** The attacking party for a camp raid, with the player's tech and talents. */
export function raidingParty(
  state: GameState,
  fleet: Partial<Record<UnitKey, number>>,
  damageBonus: number,
) {
  return { units: fleet, shieldTech: state.research.shieldHarmonics, damageBonus };
}

/** Ships the player may actually send on an offensive mission. */
export const COMBAT_CAPABLE: UnitKey[] = (Object.keys(SHIPS) as UnitKey[]).filter(
  (key) => key !== 'probe' && key !== 'colonyShip',
);
